import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';
import { ArrowLeft, Download } from 'lucide-react';
import RatingControl from '../components/RatingControl';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const SecondLevelReviews = () => {
  const { user } = useAuth();
  const [appraisals, setAppraisals] = useState([]);
  const [selectedAppraisal, setSelectedAppraisal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  useEffect(() => {
    fetchAppraisals();
  }, []);

  const fetchAppraisals = async () => {
    try {
      const response = await axios.get(`${API_URL}/appraisals`);
      const secondLevelAppraisals = response.data.filter(a => 
        a.second_level_manager_id === user.id
      );
      setAppraisals(secondLevelAppraisals);
    } catch (error) {
      toast.error('Failed to load 2nd-level reviews');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveCalibration = async () => {
    try {
      await axios.put(`${API_URL}/appraisals/${selectedAppraisal.id}`, selectedAppraisal);
      toast.success('Calibration saved successfully');
      fetchAppraisals();
    } catch (error) {
      toast.error('Failed to save calibration');
    }
  };

  const handleFinalizeAppraisal = async () => {
    try {
      await axios.put(`${API_URL}/appraisals/${selectedAppraisal.id}`, {
        ...selectedAppraisal,
        status: 'FINALIZED'
      });
      toast.success('Appraisal finalized successfully');
      setSelectedAppraisal(null);
      fetchAppraisals();
    } catch (error) {
      toast.error('Failed to finalize appraisal');
    }
  };
  
  const handleDownloadPdf = async () => {
    setDownloadingPdf(true);
    try {
      const response = await axios.get(
        `${API_URL}/appraisals/${selectedAppraisal.id}/pdf`,
        { responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Appraisal_${selectedAppraisal.employee_name.replace(/\s/g, '_')}_${selectedAppraisal.cycle_name.replace(/\s/g, '_')}_${selectedAppraisal.cycle_year}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('PDF downloaded successfully');
    } catch (error) {
      toast.error('Failed to download PDF');
    } finally {
      setDownloadingPdf(false);
    }
  };

  const getStatusBadge = (status) => {
    const labels = {
      'DRAFT_SELF': 'Draft',
      'SUBMITTED_SELF': 'Pending Manager',
      'IN_MANAGER_REVIEW': 'Manager Review',
      'SECOND_LEVEL_REVIEW': 'Calibration',
      'FINALIZED': 'Finalized'
    };

    return (
      <Badge variant="outline" className="uppercase tracking-wider text-xs border border-slate-200" data-testid={`status-${status}`}>
        {labels[status]}
      </Badge>
    );
  };

  if (loading) return <div className="text-sm text-slate-700">Loading...</div>;

  if (selectedAppraisal) {
    const isEditable = selectedAppraisal.status === 'SECOND_LEVEL_REVIEW';

    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl tracking-tight font-semibold text-slate-900" data-testid="second-level-title">2nd-Level Calibration</h1>
            <p className="text-sm text-slate-700 mt-2">{selectedAppraisal.employee_name} - {selectedAppraisal.cycle_name} {selectedAppraisal.cycle_year}</p>
          </div>
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={handleDownloadPdf}
              disabled={downloadingPdf}
              className="border-slate-200 rounded-sm"
              data-testid="download-pdf-button"
            >
              <Download className="mr-2 h-4 w-4" />
              {downloadingPdf ? 'Downloading...' : 'Download PDF'}
            </Button>
            <Button
              variant="outline"
              onClick={() => setSelectedAppraisal(null)}
              className="border-slate-200 rounded-sm"
              data-testid="back-button"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to List
            </Button>
          </div>
        </div>

        <Card className="border border-slate-200 bg-white rounded-sm p-6 space-y-6">
          {/* Employee & Manager Reviews (Read-only) */}
          <div>
            <h3 className="text-xl font-medium text-slate-900 mb-4">Employee and Manager Assessments</h3>
            <div className="space-y-6">
              {selectedAppraisal.competencies.map((comp, idx) => (
                <div key={idx} className="border border-slate-200 bg-slate-50 p-4 rounded-sm" data-testid={`competency-${idx}`}>
                  <div className="font-semibold text-slate-900 mb-3">{comp.name}</div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <div className="text-xs text-slate-500 mb-1">Employee Rating</div>
                      <div className="text-slate-900 font-semibold">{comp.self_rating || '-'}</div>
                    </div>
                    <div>
                      <div className="text-xs text-slate-500 mb-1">Employee Comments</div>
                      <div className="text-sm text-slate-700">{comp.self_comment || '-'}</div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4 pt-4 border-t border-slate-200">
                    <div>
                      <div className="text-xs text-slate-500 mb-1">Manager Rating</div>
                      <div className="text-slate-900 font-semibold">{comp.manager_rating || '-'}</div>
                    </div>
                    <div>
                      <div className="text-xs text-slate-500 mb-1">Manager Comments</div>
                      <div className="text-sm text-slate-700">{comp.manager_comment || '-'}</div>
                    </div>
                  </div>

                  {/* 2nd Level Calibration */}
                  <div className="pt-4 border-t border-slate-200">
                    <div className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 mb-3">Your Calibration</div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 mb-2 block">Calibrated Rating</Label>
                        {isEditable ? (
                          <RatingControl
                            value={comp.second_level_rating}
                            onChange={(rating) => {
                              const updated = { ...selectedAppraisal };
                              updated.competencies[idx].second_level_rating = rating;
                              setSelectedAppraisal(updated);
                            }}
                            testId={`second-level-rating-${idx}`}
                          />
                        ) : (
                          <div className="text-slate-900 font-semibold">{comp.second_level_rating || '-'}</div>
                        )}
                      </div>
                      
                      <div>
                        <Label className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 mb-2 block">Calibration Notes</Label>
                        {isEditable ? (
                          <Textarea
                            value={comp.second_level_comment || ''}
                            onChange={(e) => {
                              const updated = { ...selectedAppraisal };
                              updated.competencies[idx].second_level_comment = e.target.value;
                              setSelectedAppraisal(updated);
                            }}
                            className="border-slate-200 rounded-sm min-h-[80px]"
                            data-testid={`second-level-comment-${idx}`}
                          />
                        ) : (
                          <div className="text-sm text-slate-700">{comp.second_level_comment || '-'}</div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Overall Summary Sections (Read-only) */}
          <div className="border-t border-slate-200 pt-6 space-y-4">
            <h3 className="text-xl font-medium text-slate-900">Overall Assessments</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-slate-50 rounded-sm border border-slate-200">
              <div>
                <div className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 mb-2">Employee Overall</div>
                <div className="text-slate-900 font-semibold">{selectedAppraisal.self_overall_rating || '-'}</div>
              </div>
              <div>
                <div className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 mb-2">Manager Overall</div>
                <div className="text-slate-900 font-semibold">{selectedAppraisal.manager_overall_rating || '-'}</div>
              </div>
            </div>
          </div>

          {/* 2nd Level Overall Calibration */}
          <div className="border-t border-slate-200 pt-6 space-y-4">
            <h3 className="text-xl font-medium text-slate-900">Final Calibration</h3>
            
            <div>
              <Label className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 mb-2 block">Final Calibrated Rating (1-5)</Label>
              {isEditable ? (
                <RatingControl
                  value={selectedAppraisal.second_level_overall_rating}
                  onChange={(rating) => setSelectedAppraisal({ ...selectedAppraisal, second_level_overall_rating: rating })}
                  testId="second-level-overall-rating"
                />
              ) : (
                <div className="text-slate-900 font-semibold">{selectedAppraisal.second_level_overall_rating || '-'}</div>
              )}
            </div>

            <div>
              <Label htmlFor="second-level-summary" className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 mb-2 block">Calibration Summary</Label>
              {isEditable ? (
                <Textarea
                  id="second-level-summary"
                  value={selectedAppraisal.second_level_summary || ''}
                  onChange={(e) => setSelectedAppraisal({ ...selectedAppraisal, second_level_summary: e.target.value })}
                  className="border-slate-200 rounded-sm min-h-[100px]"
                  data-testid="second-level-summary-input"
                />
              ) : (
                <div className="text-sm text-slate-700">{selectedAppraisal.second_level_summary || '-'}</div>
              )}
            </div>

            <div>
              <Label htmlFor="decision-notes" className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 mb-2 block">Decision Notes</Label>
              {isEditable ? (
                <Textarea
                  id="decision-notes"
                  value={selectedAppraisal.second_level_decision_notes || ''}
                  onChange={(e) => setSelectedAppraisal({ ...selectedAppraisal, second_level_decision_notes: e.target.value })}
                  className="border-slate-200 rounded-sm min-h-[100px]"
                  data-testid="decision-notes-input"
                />
              ) : (
                <div className="text-sm text-slate-700">{selectedAppraisal.second_level_decision_notes || '-'}</div>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          {isEditable && (
            <div className="flex gap-4 border-t border-slate-200 pt-6">
              <Button
                onClick={handleSaveCalibration}
                variant="outline"
                className="border-slate-200 rounded-sm"
                data-testid="save-calibration-button"
              >
                Save Calibration
              </Button>
              <Button
                onClick={handleFinalizeAppraisal}
                className="bg-[#16A34A] hover:bg-[#15803D] text-white rounded-sm"
                data-testid="finalize-appraisal-button"
              >
                Finalize Appraisal
              </Button>
            </div>
          )}
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-4xl tracking-tight font-semibold text-slate-900" data-testid="second-level-reviews-title">2nd-Level Reviews</h1>
        <p className="text-sm text-slate-700 mt-2">As a super-manager, calibrate manager ratings and finalize appraisals.</p>
      </div>

      <Card className="border border-slate-200 bg-white rounded-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="second-level-reviews-table">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left p-4 text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Employee</th>
                <th className="text-left p-4 text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Manager</th>
                <th className="text-left p-4 text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Cycle</th>
                <th className="text-left p-4 text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Status</th>
                <th className="text-left p-4 text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Manager Overall</th>
                <th className="text-left p-4 text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">2nd-Level Overall</th>
                <th className="text-left p-4 text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Updated</th>
                <th className="text-left p-4 text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Action</th>
              </tr>
            </thead>
            <tbody>
              {appraisals.length === 0 ? (
                <tr>
                  <td colSpan="8" className="text-center p-8 text-slate-500">No 2nd-level reviews found</td>
                </tr>
              ) : (
                appraisals.map((appraisal) => (
                  <tr key={appraisal.id} className="border-b border-slate-200 hover:bg-slate-50 transition-colors duration-200" data-testid={`second-level-row-${appraisal.id}`}>
                    <td className="p-4 text-slate-700">{appraisal.employee_name}</td>
                    <td className="p-4 text-slate-700">{appraisal.manager_name}</td>
                    <td className="p-4 text-slate-700">{appraisal.cycle_name} {appraisal.cycle_year}</td>
                    <td className="p-4">{getStatusBadge(appraisal.status)}</td>
                    <td className="p-4 text-slate-900 font-semibold">{appraisal.manager_overall_rating || '-'}</td>
                    <td className="p-4 text-slate-900 font-semibold">{appraisal.second_level_overall_rating || '-'}</td>
                    <td className="p-4 text-slate-500">{formatDistanceToNow(new Date(appraisal.updated_at), { addSuffix: true })}</td>
                    <td className="p-4">
                      <Button
                        onClick={() => setSelectedAppraisal(appraisal)}
                        variant="outline"
                        size="sm"
                        className="border-slate-200 rounded-sm"
                        data-testid={`calibrate-appraisal-${appraisal.id}`}
                      >
                        Calibrate
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
};

export default SecondLevelReviews;
